/**
 * CardSlider - A responsive card slider with infinite loop on desktop and swipe navigation on mobile
 * Features:
 * - Desktop: Infinite horizontal loop with CSS animations
 * - Mobile: Touch swipe navigation with arrow buttons
 * - Responsive design with automatic mode switching
 */
class CardSlider extends HTMLElement {
  // Class constants
  static DESKTOP_BREAKPOINT = 750;
  static SWIPE_THRESHOLD = 0.3; // 30% of item width
  static TOUCH_MOVE_THRESHOLD = 10; // px
  static GAP_SIZE = 24; // px

  constructor() {
    super();

    // DOM elements
    this.container = this.querySelector(".card-slider__container");
    this.list = this.querySelector(".card-slider__list");
    this.allItems = this.querySelectorAll(".card-slider__item");
    this.originalItems = this.querySelectorAll(
      ".card-slider__item:not(.card-slider__item--duplicate)"
    );
    this.prevButton = this.querySelector(".card-slider__arrow--prev");
    this.nextButton = this.querySelector(".card-slider__arrow--next");

    // State
    this.currentIndex = 0;
    this.itemWidth = 0;
    this.isDesktop = window.innerWidth >= CardSlider.DESKTOP_BREAKPOINT;

    // Touch state
    this.touchState = {
      isDragging: false,
      startX: 0,
      currentX: 0,
      hasMoved: false,
    };

    // Bind methods
    this.handleResize = this.handleResize.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleScroll = this.handleScroll.bind(this);

    this.init();
  }

  /**
   * Initialize the slider
   */
  init() {
    if (!this.isValidSetup()) return;

    this.calculateDimensions();
    this.setupEventListeners();
    this.updateUI();

    this.logDebugInfo();
  }

  /**
   * Validate required elements exist
   */
  isValidSetup() {
    return this.list && this.originalItems.length > 0;
  }

  /**
   * Calculate item dimensions based on current viewport
   */
  calculateDimensions() {
    if (!this.originalItems.length || !this.container) return;

    if (this.isDesktop) {
      // Desktop: Use actual item width with gap
      const firstItem = this.originalItems[0];
      const style = getComputedStyle(firstItem);
      const margin = parseInt(style.marginRight) || CardSlider.GAP_SIZE;
      this.itemWidth = firstItem.offsetWidth + margin;
    } else {
      // Mobile: Use container's full width
      this.itemWidth = this.container.clientWidth;
    }
  }

  /**
   * Set up all event listeners
   */
  setupEventListeners() {
    // Remove existing listeners to prevent duplicates
    this.removeEventListeners();

    // Button navigation
    this.setupButtonListeners();

    // Touch navigation (mobile only)
    if (!this.isDesktop) {
      this.setupTouchListeners();
      this.setupScrollListener();
    }

    // Window resize
    window.addEventListener("resize", this.handleResize);
  }

  /**
   * Setup button event listeners
   */
  setupButtonListeners() {
    if (this.prevButton && this.nextButton) {
      this.prevButton.addEventListener("click", () => this.goToPrev());
      this.nextButton.addEventListener("click", () => this.goToNext());
    }
  }

  /**
   * Setup touch event listeners for mobile swipe
   */
  setupTouchListeners() {
    if (!this.container) return;

    this.container.addEventListener("touchstart", this.handleTouchStart, {
      passive: true,
    });
    this.container.addEventListener("touchmove", this.handleTouchMove, {
      passive: false,
    });
    this.container.addEventListener("touchend", this.handleTouchEnd, {
      passive: true,
    });
  }

  /**
   * Setup scroll listener for mobile
   */
  setupScrollListener() {
    if (this.container) {
      this.container.addEventListener("scroll", this.handleScroll);
    }
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    window.removeEventListener("resize", this.handleResize);

    if (this.container) {
      this.container.removeEventListener("touchstart", this.handleTouchStart);
      this.container.removeEventListener("touchmove", this.handleTouchMove);
      this.container.removeEventListener("touchend", this.handleTouchEnd);
      this.container.removeEventListener("scroll", this.handleScroll);
    }
  }

  /**
   * Handle touch start event
   */
  handleTouchStart(e) {
    this.touchState.startX = e.touches[0].clientX;
    this.touchState.currentX = this.touchState.startX;
    this.touchState.isDragging = true;
    this.touchState.hasMoved = false;

    // Disable smooth scrolling during drag
    this.setScrollBehavior("auto");
  }

  /**
   * Handle touch move event
   */
  handleTouchMove(e) {
    if (!this.touchState.isDragging) return;

    this.touchState.currentX = e.touches[0].clientX;
    const deltaX = this.touchState.startX - this.touchState.currentX;
    this.touchState.hasMoved = true;

    // Prevent page scrolling for significant horizontal movement
    if (Math.abs(deltaX) > CardSlider.TOUCH_MOVE_THRESHOLD) {
      e.preventDefault();
    }
  }

  /**
   * Handle touch end event
   */
  handleTouchEnd() {
    if (!this.touchState.isDragging) return;

    this.touchState.isDragging = false;
    this.setScrollBehavior("smooth");

    if (this.touchState.hasMoved) {
      this.processSwipeGesture();
    }

    // Reset touch state
    this.resetTouchState();
  }

  /**
   * Process swipe gesture and navigate accordingly
   */
  processSwipeGesture() {
    const deltaX = this.touchState.startX - this.touchState.currentX;
    const threshold = this.itemWidth * CardSlider.SWIPE_THRESHOLD;

    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0) {
        this.goToNext(); // Swiped left
      } else {
        this.goToPrev(); // Swiped right
      }
    } else {
      // Insufficient movement, return to current position
      this.goToSlide(this.currentIndex);
    }
  }

  /**
   * Handle scroll event for mobile button updates
   */
  handleScroll() {
    this.updateCurrentIndexFromScroll();
    this.updateButtons();
  }

  /**
   * Set scroll behavior for container
   */
  setScrollBehavior(behavior) {
    if (this.container) {
      this.container.style.scrollBehavior = behavior;
    }
  }

  /**
   * Reset touch state
   */
  resetTouchState() {
    this.touchState = {
      isDragging: false,
      startX: 0,
      currentX: 0,
      hasMoved: false,
    };
  }

  /**
   * Update current index based on scroll position
   */
  updateCurrentIndexFromScroll() {
    if (this.container && this.itemWidth > 0) {
      this.currentIndex = Math.round(
        this.container.scrollLeft / this.itemWidth
      );
    }
  }

  /**
   * Navigate to previous slide
   */
  goToPrev() {
    if (this.canGoPrev()) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  /**
   * Navigate to next slide
   */
  goToNext() {
    if (this.canGoNext()) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  /**
   * Navigate to specific slide
   */
  goToSlide(index) {
    if (!this.isValidIndex(index)) return;

    this.currentIndex = index;
    const scrollLeft = index * this.itemWidth;

    if (this.container) {
      this.container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }

    this.updateButtons();
  }

  /**
   * Check if can navigate to previous slide
   */
  canGoPrev() {
    return this.currentIndex > 0;
  }

  /**
   * Check if can navigate to next slide
   */
  canGoNext() {
    return this.currentIndex < this.originalItems.length - 1;
  }

  /**
   * Validate slide index
   */
  isValidIndex(index) {
    return index >= 0 && index < this.originalItems.length;
  }

  /**
   * Update button states and visibility
   */
  updateButtons() {
    if (!this.prevButton || !this.nextButton) return;

    if (this.isDesktop) {
      this.hideButtons();
    } else {
      this.showAndUpdateButtons();
    }
  }

  /**
   * Hide navigation buttons
   */
  hideButtons() {
    this.prevButton.style.display = "none";
    this.nextButton.style.display = "none";
  }

  /**
   * Show and update navigation buttons
   */
  showAndUpdateButtons() {
    // Show buttons
    this.prevButton.style.display = "flex";
    this.nextButton.style.display = "flex";

    // Update states
    this.prevButton.disabled = !this.canGoPrev();
    this.nextButton.disabled = !this.canGoNext();

    // Update accessibility labels
    this.updateAriaLabels();
  }

  /**
   * Update ARIA labels for accessibility
   */
  updateAriaLabels() {
    const current = this.currentIndex + 1;
    const total = this.originalItems.length;

    this.prevButton.setAttribute(
      "aria-label",
      `Previous slide, ${current} of ${total}`
    );
    this.nextButton.setAttribute(
      "aria-label",
      `Next slide, ${current} of ${total}`
    );
  }

  /**
   * Update all UI elements
   */
  updateUI() {
    this.updateButtons();
  }

  /**
   * Handle window resize
   */
  handleResize() {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.innerWidth >= CardSlider.DESKTOP_BREAKPOINT;

    if (wasDesktop !== this.isDesktop) {
      // Mode changed, reinitialize
      this.handleModeChange();
    } else {
      // Same mode, just recalculate
      this.calculateDimensions();
      this.updateUI();
    }
  }

  /**
   * Handle switching between mobile and desktop modes
   */
  handleModeChange() {
    this.calculateDimensions();
    this.setupEventListeners();
    this.resetPosition();
    this.updateUI();
  }

  /**
   * Reset slider to initial position
   */
  resetPosition() {
    if (this.container) {
      this.container.scrollLeft = 0;
      this.currentIndex = 0;
    }
  }

  /**
   * Log debug information
   */
  logDebugInfo() {
    console.log("CardSlider initialized:", {
      isDesktop: this.isDesktop,
      itemWidth: this.itemWidth,
      originalItemsCount: this.originalItems.length,
      containerWidth: this.container?.clientWidth,
    });
  }

  /**
   * Cleanup when element is removed
   */
  disconnectedCallback() {
    this.removeEventListeners();
  }
}

// Initialize slider
if (typeof customElements !== "undefined") {
  customElements.define("card-slider", CardSlider);
}

// Fallback initialization for older browsers
document.addEventListener("DOMContentLoaded", () => {
  const cardSliders = document.querySelectorAll(".card-slider");
  cardSliders.forEach((slider) => {
    if (!slider.cardSliderInitialized) {
      new CardSlider.call(slider);
      slider.cardSliderInitialized = true;
    }
  });
});
